import { local } from '../storage';
import logoUrl from '../assets/nlm-logo.webp';
import {
  Activity, Building2, ChevronDown, ChevronRight, CircleHelp, PanelLeftClose, PanelLeftOpen,
  BriefcaseBusiness, Command, Download, LogOut, Mail, Menu, Palette, Search, Settings, X,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth';
import { useApi } from '../hooks';
import { AGENCY_GROUPS, AGENCY_PATHS, CLIENT_PATHS, currentEntry, entryPages } from '../nav';
import { SIDEBAR_MODE_KEY, clientIdPage, isSidebarMode, pageMatches, resolveMode, routeMode, type SidebarMode } from '../sidebar-mode';
import ClientNav from './ClientNav';
import { useClientSelection } from '../client-context';
import { useInstallPrompt } from '../install';
import CommandPalette from './CommandPalette';
import PageHelpButton from './PageHelp';
import SectionTabs from './SectionTabs';
import UpdateBanner from './UpdateBanner';
import { shortcutLabel } from '../shortcut';
import { roleLabel } from '../roles';
import { monogram } from './Ui';

/**
 * Agency | Clients. Which half shows is decided in sidebar-mode.ts: the tab you
 * clicked (a peek that does not navigate), then the route, then your last
 * choice on pages that belong to neither, then Agency.
 */
function useSidebarMode(pathname: string) {
  const [remembered, setRemembered] = useState<SidebarMode | undefined>(() => {
    try { const v = localStorage.getItem(SIDEBAR_MODE_KEY); return isSidebarMode(v) ? v : undefined; } catch { return undefined; }
  });
  const [override, setOverride] = useState<SidebarMode | undefined>();
  const [lastPath, setLastPath] = useState(pathname);
  // Reset during render, not in an effect, so leaving a page never flashes the stale half.
  if (lastPath !== pathname) { setLastPath(pathname); setOverride(undefined); }
  const mode = resolveMode(override, routeMode(pathname, CLIENT_PATHS, AGENCY_PATHS), remembered);
  const setMode = useCallback((m: SidebarMode) => {
    setOverride(m);
    setRemembered(m);
    try { localStorage.setItem(SIDEBAR_MODE_KEY, m); } catch { /* storage blocked: keep the in-memory choice */ }
  }, []);
  return { mode, setMode };
}

export default function Layout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const { client } = useClientSelection();
  // Counts only: the full pending list (bodies and notes included) used to load on every page for this badge.
  const summary = useApi<{ critical: string | number; high: string | number; attention?: string | number }>('/api/tasks/summary');
  useEffect(() => {const update=()=>summary.reload();window.addEventListener('nl-aos-tasks-changed',update);return()=>window.removeEventListener('nl-aos-tasks-changed',update);},[summary.reload]);
  // Urgent work plus client requests nobody has picked up yet (older servers send no attention count).
  const urgentCount = summary.data?.attention != null ? Number(summary.data.attention) : Number(summary.data?.critical || 0) + Number(summary.data?.high || 0);
  const health = useApi<{ status: string }>('/api/healthz');
  const location = useLocation();
  const idPage = clientIdPage(location.pathname);
  const current = currentEntry(location.pathname, location.search);
  const onSettings = location.pathname === '/settings';
  const onAllClients = location.pathname === '/clients';
  const onProfile = location.pathname.startsWith('/clients/');
  const clientContext = onAllClients || onProfile || idPage !== null || CLIENT_PATHS.includes(location.pathname);
  // Agency crumbs follow the sidebar: a page that shares an entry with others names that entry ("Portfolio",
  // "CRM"); a page of its own shows no crumb. They used to all start with "Agency", a word the sidebar uses for its mode switch.
  const agencyParent = !clientContext && !onSettings && current?.item.tabs && current.page.label !== current.item.label ? current.item : null;
  // A long sidebar scrolls; the page you are on must never sit hidden under the fold. The list
  // scrolls only as far as it must, then on to a whole row, so nothing (the client switcher, a
  // link) is left cut in half under the Agency | Clients switch. Only the list scrolls, never the page.
  useEffect(() => {
    const active = document.querySelector<HTMLElement>('.sidebar .primary-nav .nav-link.active');
    const nav = active?.closest<HTMLElement>('.primary-nav');
    if (!active || !nav || nav.scrollHeight <= nav.clientHeight || active.closest('.nav-pinned')) return;
    // The client picker is pinned at the list's top (shell.css): rows under it count as hidden.
    const pinned = nav.querySelector<HTMLElement>(':scope > .nav-pinned');
    const visibleTop = () => (pinned ? pinned.getBoundingClientRect().bottom : nav.getBoundingClientRect().top);
    const box = nav.getBoundingClientRect();
    const row = active.getBoundingClientRect();
    if (row.top >= visibleTop() && row.bottom <= box.bottom) return;
    nav.scrollTop += row.bottom > box.bottom ? row.bottom - box.bottom + 8 : row.top - visibleTop() - 8;
    if (row.bottom <= box.bottom) return;
    const top = visibleTop();
    const cut = [...nav.querySelectorAll<HTMLElement>('.nav-link, .nav-group-label, .client-nav-head, .client-nav-search, .client-nav-note')]
      .filter((el) => !pinned?.contains(el))
      .map((el) => el.getBoundingClientRect())
      .find((r) => r.top < top && r.bottom > top);
    if (cut) nav.scrollTop += cut.bottom - top;
    // client?.id: a client's links only exist once its record loads, after the route changed.
  }, [location.pathname, client?.id]);
  // A nav taller than the window fades at its foot while more sits below (shell.css [data-scrolls]):
  // a group label cut in half at the fold read as a mistake.
  useEffect(() => {
    const nav = document.querySelector<HTMLElement>('.sidebar .primary-nav');
    if (!nav) return;
    const mark = () => nav.toggleAttribute('data-scrolls', nav.scrollHeight > nav.clientHeight + 1 && nav.scrollTop + nav.clientHeight < nav.scrollHeight - 1);
    const raf = window.requestAnimationFrame(mark);
    nav.addEventListener('scroll', mark, { passive: true });
    window.addEventListener('resize', mark);
    return () => { window.cancelAnimationFrame(raf); nav.removeEventListener('scroll', mark); window.removeEventListener('resize', mark); };
  }, [location.pathname, client?.id]);
  const { mode, setMode } = useSidebarMode(location.pathname);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileDialog = useRef<HTMLDialogElement>(null);
  const [collapsed, setCollapsed] = useState(() => local.get('nl-aos-sidebar-collapsed') === 'true');
  const [searchOpen, setSearchOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const install = useInstallPrompt();

  useEffect(() => { setMobileOpen(false); setUserMenuOpen(false); window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [location.pathname]);
  useEffect(() => {
    const dialog = mobileDialog.current;
    if (!mobileOpen || !dialog) return;
    const previous = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = 'hidden';
    const desktop = matchMedia('(min-width: 821px)');
    const closeOnDesktop = () => { if (desktop.matches) setMobileOpen(false); };
    desktop.addEventListener('change', closeOnDesktop);
    return () => {
      desktop.removeEventListener('change', closeOnDesktop);
      dialog.close();
      document.body.style.overflow = previousOverflow;
      previous?.focus();
    };
  }, [mobileOpen]);
  useEffect(() => local.set('nl-aos-sidebar-collapsed', String(collapsed)), [collapsed]);
  useEffect(() => {
    const openSearch = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        if (document.querySelector('dialog[open]')) return;
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener('keydown', openSearch);
    return () => document.removeEventListener('keydown', openSearch);
  }, []);
  useEffect(() => {
    const closeMenu = (event: MouseEvent) => {
      if (!userMenuRef.current?.contains(event.target as Node)) setUserMenuOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', closeMenu);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeMenu);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, []);

  const renderNav = (mobile = false) => {
    const hideLabels = collapsed && !mobile;
    return <>
      <div className="brand-row">
        <img className="brand-logo" src={logoUrl} alt="No Limits Marketing" />
        {!hideLabels && <span className="brand-wordmark" aria-hidden="true"><strong>NOLA</strong><small>Agency OS</small></span>}
        {!mobile && <button className="sidebar-collapse" onClick={() => setCollapsed(value => !value)} aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'} title={collapsed ? 'Expand navigation' : 'Collapse navigation'}>
          {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
        </button>}
        <button className="icon-button mobile-close" aria-label="Close navigation" onClick={() => setMobileOpen(false)}><X aria-hidden="true" /></button>
      </div>
      <div className={`sidebar-mode ${hideLabels ? 'icons' : ''}`} data-mode={mode} role="tablist" aria-label="Sidebar section">
        {(['agency', 'clients'] as const).map(m => (
          <button key={m} type="button" role="tab" aria-selected={mode === m} className={mode === m ? 'active' : ''} data-mode={m} onClick={() => setMode(m)} aria-label={m === 'agency' ? 'Agency' : 'Clients'} title={hideLabels ? (m === 'agency' ? 'Agency' : 'Clients') : undefined}>
            {hideLabels ? (m === 'agency' ? <BriefcaseBusiness aria-hidden="true" /> : <Building2 aria-hidden="true" />) : (m === 'agency' ? 'Agency' : 'Clients')}
          </button>
        ))}
      </div>
      {mode === 'agency' ? <nav className="primary-nav" aria-label="Agency navigation">
        {AGENCY_GROUPS.map(group => <div className="nav-group" key={group.label}>
          {!hideLabels && <p className="nav-group-label">{group.label}</p>}
          {group.items.map(item => { const { to, label, icon: Icon, end } = item; return (
            <NavLink key={to} to={to} end={end} onClick={() => { if (mobile) setMobileOpen(false); }} className={({ isActive }) => isActive || (item.tabs && entryPages(item).some(p => pageMatches(location.pathname, location.search, p, item.tabs))) ? 'nav-link active' : 'nav-link'} aria-label={label} title={hideLabels ? label : undefined}>
              <Icon aria-hidden="true" />{!hideLabels && <span>{label}</span>}{!hideLabels && to === '/tasks' && urgentCount > 0 && <span className="nav-count" aria-label={`${urgentCount} tasks need attention`}>{urgentCount}</span>}
            </NavLink>
          ); })}
        </div>)}
      </nav> : <ClientNav hideLabels={hideLabels} onNavigate={() => { if (mobile) setMobileOpen(false); }} />}
      <nav className="secondary-nav" aria-label="Workspace navigation">
        <NavLink to="/settings" onClick={() => { if (mobile) setMobileOpen(false); }} className={({isActive}) => isActive ? "nav-link active" : "nav-link"} aria-label="Settings" title={hideLabels ? "Settings" : undefined}><Settings aria-hidden="true"/>{!hideLabels && <span>Settings</span>}</NavLink>
      </nav>
    </>;
  };

  return (
    <div className={`app-shell ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <a className="skip-link" href="#main-content">Skip to main content</a>
      <aside className="sidebar">{renderNav()}</aside>
      <dialog ref={mobileDialog} className="mobile-sidebar" aria-label="Workspace navigation" onCancel={() => setMobileOpen(false)} onClick={event => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) setMobileOpen(false);
      }}>{renderNav(true)}</dialog>
      <div className="app-column">
        <header className="topbar">
          <button className="icon-button menu-button" aria-label="Open navigation" aria-haspopup="dialog" aria-expanded={mobileOpen} onClick={() => setMobileOpen(true)}><Menu aria-hidden="true" /></button>
          {/* The trail names what sits above the page (Clients, the client, a sidebar entry's group); the page
              names itself in its own heading and tabs, so the bar never repeats it. */}
          <nav className="workspace-breadcrumb" aria-label="Breadcrumb">{clientContext && !onAllClients && <Link to="/clients">Clients</Link>}{clientContext && client && !onAllClients && !onProfile && <><ChevronRight aria-hidden="true"/><Link to={`/clients/${client.id}`} className="breadcrumb-client" aria-current="location">{client.name}</Link></>}{agencyParent && <Link to={agencyParent.to}>{agencyParent.label}</Link>}</nav>
          <button className="global-search" type="button" onClick={() => setSearchOpen(true)} aria-label="Search workspace" aria-haspopup="dialog" aria-expanded={searchOpen}>
            <Search aria-hidden="true" />
            <span>Search workspace</span>
            <kbd>{shortcutLabel() === '⌘K' ? <><Command aria-hidden="true" />K</> : 'Ctrl K'}</kbd>
          </button>
          <div className="topbar-actions">
            <PageHelpButton />
            {health.error && <div className="sync-state offline" role="status" title={health.error}><Activity aria-hidden="true" /><span>Connection issue</span></div>}
            <div className="user-menu" ref={userMenuRef}>
              <button className="user-menu-trigger" aria-label="Open account menu" aria-haspopup="menu" aria-expanded={userMenuOpen} onClick={() => setUserMenuOpen(value => !value)}>
                <span className="avatar" aria-hidden="true">{monogram(user?.name) || 'NL'}</span>
                <span className="user-copy"><strong>{user?.name}</strong><small>{roleLabel(user?.role)}</small></span>
                <ChevronDown className="user-chevron" aria-hidden="true" />
              </button>
              {userMenuOpen && <div className="user-menu-popover" role="menu">
                <div className="user-menu-identity"><span className="avatar" aria-hidden="true">{monogram(user?.name) || 'NL'}</span><span><strong>{user?.name}</strong><small>{user?.email}</small></span></div>
                <div className="user-menu-separator" />
                <Link to="/settings" role="menuitem" onClick={() => setUserMenuOpen(false)}><Settings aria-hidden="true" /><span><strong>Settings</strong></span></Link>
                <Link to="/settings#appearance" role="menuitem" onClick={() => setUserMenuOpen(false)}><Palette aria-hidden="true"/><span><strong>Appearance</strong></span></Link>
                <Link to="/settings#my-google" role="menuitem" onClick={() => setUserMenuOpen(false)}><Mail aria-hidden="true"/><span><strong>Your Gmail and Calendar</strong></span></Link>
                <a href="mailto:support@nolimits.marketing" role="menuitem" onClick={() => setUserMenuOpen(false)}><CircleHelp aria-hidden="true"/><span><strong>Contact support</strong><small>support@nolimits.marketing</small></span></a>
                {install.available && <button role="menuitem" onClick={() => { setUserMenuOpen(false); void install.prompt(); }}><Download aria-hidden="true" /><span><strong>Install app</strong></span></button>}
                <div className="user-menu-separator" />
                <button className="signout-action" role="menuitem" onClick={logout}><LogOut aria-hidden="true" /><span><strong>Sign out</strong></span></button>
              </div>}
            </div>
          </div>
        </header>
        <main id="main-content" className={location.pathname.startsWith('/knowledge') ? 'main-content wide' : 'main-content'} tabIndex={-1}><SectionTabs />{children}</main>
      </div>
      {searchOpen && <CommandPalette onClose={() => setSearchOpen(false)} />}
      <UpdateBanner changelog />
    </div>
  );
}
